"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SingleSelectField = void 0;
const i18n_1 = require("../../../exports/i18n");
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const utils_1 = require("../../../model/utils");
const color_1 = require("../../../model/color");
const field_types_1 = require("../../../types/field_types");
const view_types_1 = require("../../../types/view_types");
const datasheet_1 = require("../../datasheet");
const common_select_field_1 = require("./common_select_field");
class SingleSelectField extends common_select_field_1.SelectField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.SingleSelect,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    validateCellValue(cv) {
        return SingleSelectField.cellValueSchema.validate(cv, { context: { field: this.field } });
    }
    validateOpenWriteValue(owv) {
        return SingleSelectField.openWriteValueSchema.validate(owv, { context: { field: this.field } });
    }
    defaultValue() {
        const defaultValue = this.field.property.defaultValue;
        if (!defaultValue || !defaultValue.trim().length) {
            return null;
        }
        return defaultValue;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.String;
    }
    get openValueJsonSchema() {
        return {
            type: 'object',
            properties: {
                id: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_option_ID),
                },
                name: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_option_name),
                },
                color: {
                    type: 'object',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_option_color),
                    properties: {
                        name: {
                            type: 'string',
                            title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_option_color_names),
                        },
                        value: {
                            type: 'string',
                            title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_option_color_values),
                        }
                    }
                },
            }
        };
    }
    get acceptFilterOperators() {
        return [
            view_types_1.FOperator.Is,
            view_types_1.FOperator.IsNot,
            view_types_1.FOperator.Contains,
            view_types_1.FOperator.DoesNotContain,
            view_types_1.FOperator.IsEmpty,
            view_types_1.FOperator.IsNotEmpty,
            view_types_1.FOperator.IsRepeat,
        ];
    }
    defaultValueForCondition(condition) {
        const { operator, value } = condition;
        if (operator === view_types_1.FOperator.Is || operator === view_types_1.FOperator.Contains) {
            // only one value will auto fill
            if (value && value.length === 1 && value[0] && this.validate(value[0])) {
                return value[0];
            }
            return null;
        }
        return null;
    }
    cellValueToStdValue(val) {
        const stdVal = {
            sourceType: field_types_1.FieldType.SingleSelect,
            data: [],
        };
        if (val) {
            const option = (0, lodash_1.find)(this.field.property.options, opt => {
                return opt.id === val;
            });
            if (!option) {
                console.error(`option: ${val} is not in field property!`, this.field.property);
                return stdVal;
            }
            stdVal.data.push({
                text: option.name,
                id: option.id,
            });
        }
        return stdVal;
    }
    stdValueToCellValue(stdValue) {
        // filter empty text
        const data = stdValue.data.filter(d => d.text);
        if (data.length === 0) {
            return null;
        }
        const currentOptionText = data[0].text;
        const option = this.field.property.options.find(opt => opt.name === currentOptionText);
        return option ? option.id : null;
    }
    cellValueToString(cellValue) {
        if (!cellValue) {
            return null;
        }
        const value = this.findOptionById(cellValue);
        return value ? value.name : null;
    }
    validate(value) {
        return (0, lodash_1.isString)(value) && this.field.property.options.some(option => option.id === value);
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        if (operator === view_types_1.FOperator.IsEmpty) {
            return cellValue == null;
        }
        if (operator === view_types_1.FOperator.IsNotEmpty) {
            return cellValue != null;
        }
        const [filterValue] = conditionValue;
        switch (operator) {
            case view_types_1.FOperator.Is: {
                return cellValue === filterValue;
            }
            case view_types_1.FOperator.IsNot: {
                return cellValue !== filterValue;
            }
            case view_types_1.FOperator.Contains: {
                return conditionValue.some(value => value === cellValue);
            }
            case view_types_1.FOperator.DoesNotContain: {
                return !conditionValue.some(value => value === cellValue);
            }
            default: {
                return super.isMeetFilter(operator, cellValue, conditionValue);
            }
        }
    }
    cellValueToApiStandardValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        if (!cellValue) {
            return null;
        }
        const value = this.findOptionById(cellValue);
        if (!value) {
            return null;
        }
        // FIXME: here's color need to transfer
        return Object.assign(Object.assign({}, value), { color: (0, color_1.getFieldOptionColor)(value.color) });
    }
    openWriteValueToCellValue(openWriteValue) {
        var _a;
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        const fieldValue = typeof openWriteValue === 'string' ? openWriteValue : (openWriteValue.id || openWriteValue.name);
        const option = this.field.property.options.find(option => ((0, common_select_field_1.isOptionId)(fieldValue) && fieldValue === option.id || fieldValue === option.name));
        return (_a = option === null || option === void 0 ? void 0 : option.id) !== null && _a !== void 0 ? _a : null;
    }
    validateUpdateOpenProperty(updateProperty, effectOption) {
        const result = SingleSelectField.openUpdatePropertySchema.validate(updateProperty);
        if (result.error) {
            return result;
        }
        return this.validateWriteOpenOptionsEffect(updateProperty, effectOption);
    }
    filterValueToOpenFilterValue(value) {
        if (Array.isArray(value)) {
            const _value = value.filter(v => this.findOptionById(v));
            return _value.length ? _value[0] : null;
        }
        return null;
    }
    openFilterValueToFilterValue(value) {
        if (value) {
            return this.findOptionById(value) ? [value] : null;
        }
        return null;
    }
    validateOpenFilterValue(value) {
        return SingleSelectField.validateOpenFilterSchema.validate(value);
    }
}
exports.SingleSelectField = SingleSelectField;
SingleSelectField.cellValueSchema = joi_1.default.custom((optionId, helpers) => {
    var _a;
    const field = (_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['field'];
    if (!(0, lodash_1.isString)(optionId)) {
        return helpers.message({ en: 'option is not string' });
    }
    if (!field.property.options.some(option => option.id === optionId)) {
        return helpers.message({ en: 'option not exist field property' });
    }
    return optionId;
}).allow(null).required();
SingleSelectField.openWriteValueSchema = joi_1.default.custom((owv, helpers) => {
    var _a;
    const field = (_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['field'];
    const optionIdOrName = (0, lodash_1.isString)(owv) ? owv : (owv.id || owv.name);
    if (!optionIdOrName) {
        return helpers.error('value format error');
    }
    if (!field.property.options.some(option => option.id === optionIdOrName || option.name === optionIdOrName)) {
        return helpers.error('option not exist field property');
    }
    return optionIdOrName;
}).allow(null).required();
SingleSelectField.openUpdatePropertySchema = joi_1.default.object({
    options: joi_1.default.array().items(joi_1.default.object({
        id: joi_1.default.string(),
        name: joi_1.default.string().required(),
        color: joi_1.default.string(),
    })).required(),
    defaultValue: joi_1.default.string()
}).required();
SingleSelectField.validateOpenFilterSchema = joi_1.default.string().allow(null);
//# sourceMappingURL=single_select_field.js.map